import React, { useState, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import Nav from "../Nav"

const PER_PAGE = 50

export default function InsuranceQuoteSubmissionsReport({ user }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || "")
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || "")
  const [email, setEmail] = useState(searchParams.get("email") || "")
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10))
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 0 })
  const [selectedSubmission, setSelectedSubmission] = useState(null)

  const updateSearchParams = (start, end, emailVal, pageNum) => {
    const params = new URLSearchParams()
    if (start) params.set("start_date", start)
    if (end) params.set("end_date", end)
    if (emailVal) params.set("email", emailVal)
    params.set("page", pageNum)
    setSearchParams(params)
  }

  const fetchData = (start, end, emailVal, pageNum = 1) => {
    setLoading(true)
    setError(null)

    const params = new URLSearchParams()
    if (start) params.append("start_date", start)
    if (end) params.append("end_date", end)
    if (emailVal) params.append("email", emailVal)
    params.append("page", pageNum)
    params.append("per_page", PER_PAGE)

    const url = `/api/reports/insurance_quote_submissions?${params}`

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((json) => {
        setData(json.data || [])
        setPagination(json.pagination || {})
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData(startDate, endDate, email, page)
  }, [])

  const handleApplyFilter = (e) => {
    e.preventDefault()
    setPage(1)
    updateSearchParams(startDate, endDate, email, 1)
    fetchData(startDate, endDate, email, 1)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    setEmail("")
    setPage(1)
    updateSearchParams("", "", "", 1)
    fetchData("", "", "", 1)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    updateSearchParams(startDate, endDate, email, newPage)
    fetchData(startDate, endDate, email, newPage)
  }

  const handleDownloadCSV = () => {
    const params = new URLSearchParams()
    if (startDate) params.append("start_date", startDate)
    if (endDate) params.append("end_date", endDate)
    if (email) params.append("email", email)
    params.append("format", "csv")

    const url = `/api/reports/insurance_quote_submissions?${params}`
    window.location.href = url
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  if (selectedSubmission) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Nav user={user} />

        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedSubmission(null)}
            className="mb-6 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to List
          </button>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Insurance Quote Submission Details</h2>
              <div className="mt-2 flex gap-6">
                <p className="text-gray-600">ID: {selectedSubmission.id}</p>
                <p className="text-gray-600">
                  Hawksoft Client ID: <span className="font-semibold text-gray-900">{selectedSubmission.hawksoft_client_id || "—"}</span>
                </p>
              </div>
            </div>

            <div className="px-5 py-6 sm:px-6">
              <div className="space-y-8">
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['email', 'phone', 'first_name', 'last_name', 'sms_consent'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined
                              ? "N/A"
                              : typeof value === "boolean"
                                ? value ? "Yes" : "No"
                                : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['address', 'city', 'state', 'zip_code'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Business Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['company_name', 'entity_type', 'years_in_business', 'years_of_experience'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Financial Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['gross_receipts', 'payroll', 'subcontracting_costs', 'material_costs', 'field_employees'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Quote Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['policy_cost', 'down_payment', 'monthly_payment', 'number_of_payments'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* ISC Information */}
                {(selectedSubmission.isc_app_id || selectedSubmission.isc_transaction_id || selectedSubmission.isc_policy_cost) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">ISC Information</h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {['isc_app_id', 'isc_transaction_id', 'isc_policy_cost', 'isc_down_payment', 'isc_monthly_payment', 'isc_number_of_payments', 'isc_error'].map(key => {
                        const value = selectedSubmission[key]
                        return (
                          <div key={key}>
                            <dt className="text-sm font-medium text-gray-600 mb-1">
                              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                            </dt>
                            <dd className="text-sm text-gray-900 break-words">
                              {value === null || value === undefined ? "N/A" : String(value)}
                            </dd>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* BTIS Information */}
                {(selectedSubmission.btis_bridge_url || selectedSubmission.btis_policy_cost || selectedSubmission.btis_carrier) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">BTIS Information</h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {['btis_carrier', 'btis_policy_cost', 'btis_down_payment', 'btis_monthly_payment', 'btis_number_of_payments'].map(key => {
                        const value = selectedSubmission[key]
                        return (
                          <div key={key}>
                            <dt className="text-sm font-medium text-gray-600 mb-1">
                              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                            </dt>
                            <dd className="text-sm text-gray-900 break-words">
                              {value === null || value === undefined ? "N/A" : String(value)}
                            </dd>
                          </div>
                        )
                      })}
                      {selectedSubmission.btis_bridge_url && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-600 mb-1">Bridge URL</dt>
                          <dd className="text-sm text-blue-600 break-words">
                            <a href={selectedSubmission.btis_bridge_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {selectedSubmission.btis_bridge_url}
                            </a>
                          </dd>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Other Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Other Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['app_id', 'entry_id', 'referred_by', 'how_did_you_hear', 'heating_equipment', 'buildings_over_3_stories'].map(key => {
                      const value = selectedSubmission[key]
                      if (value === null || value === undefined) return null
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['created_at', 'updated_at'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-500 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-600">
                            {value ? new Date(value).toLocaleString() : "N/A"}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Nav user={user} />

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-2">
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">Insurance Quote Submissions</span>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Insurance Quote Submissions</h2>
                <p className="mt-2 text-gray-600">
                  Total: {pagination.total_count || 0} submission{pagination.total_count !== 1 ? "s" : ""}
                </p>
              </div>
              {selectedSubmission && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Hawksoft Client ID</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedSubmission.hawksoft_client_id || "—"}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-6 sm:px-6 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleApplyFilter} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    placeholder="Search by email..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleDownloadCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm"
                >
                  📥 Download CSV
                </button>
              </div>
            </form>
          </div>

          <div className="overflow-x-auto">
            {loading && (
              <div className="px-6 py-8 text-center text-gray-600">
                Loading...
              </div>
            )}

            {error && (
              <div className="px-6 py-8 text-center text-red-600">
                Error: {error}
              </div>
            )}

            {!loading && !error && data?.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-600">
                No data available
              </div>
            )}

            {!loading && !error && data?.length > 0 && (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-40">Email</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-32">Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-32">Company</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">ISC / BTIS</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-32">Policy Cost</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">Submitted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedSubmission(row)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.email}>{row.email}</td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={`${row.first_name} ${row.last_name}`}>
                          {row.first_name} {row.last_name}
                        </td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.company_name || "N/A"}>{row.company_name || "N/A"}</td>
                        <td className="px-4 py-3 text-gray-700 text-xs" title={`ISC: ${row.isc_app_id || "—"} | BTIS: ${row.btis_carrier || "—"}`}>
                          <div>ISC: {row.isc_app_id || "—"}</div>
                          <div>BTIS: {row.btis_carrier || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-xs" title={`ISC: ${row.isc_policy_cost || "—"} | BTIS: ${row.btis_policy_cost || "—"}`}>
                          <div>ISC: {row.isc_policy_cost ? `$${row.isc_policy_cost}` : "—"}</div>
                          <div>BTIS: {row.btis_policy_cost ? `$${row.btis_policy_cost}` : "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Page {page} of {pagination.total_pages || 1}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.total_pages, page + 1))}
                      disabled={page === pagination.total_pages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
